import { ApiError } from '@/lib/api'
import type { Media, MediaKind, UploadIntent, UploadPartUrl } from '@/types/api'
import { failureText, isTerminalStatus, kindForFile, mimeForFile } from '../lib/display'
import { isUrlUsable, partLength, partNumbersToRequest, partRange, retryDelay } from './parts'

/*
 * Upload manager — framework-free so it can be unit tested without React.
 *
 *   queued → hashing → starting (intent) → uploading (parts) → completing → processing → ready
 *                                  ↘ duplicate (early, sha256 match)      ↘ failed | rejected
 *   any active phase → canceled (XHRs aborted + DELETE /media/uploads/{id})
 *
 * All parts of all uploads share one queue with `maxParallelParts` slots (4), so
 * many small files and one big video use the network evenly; earlier files go first.
 */

export type UploadPhase =
  | 'queued'
  | 'hashing'
  | 'starting'
  | 'uploading'
  | 'completing'
  | 'processing'
  | 'ready'
  | 'duplicate'
  | 'failed'
  | 'rejected'
  | 'canceled'

export const ACTIVE_PHASES: ReadonlySet<UploadPhase> = new Set(['queued', 'hashing', 'starting', 'uploading', 'completing'])
export const FINISHED_PHASES: ReadonlySet<UploadPhase> = new Set(['ready', 'duplicate', 'failed', 'rejected', 'canceled'])

export interface UploadErrorInfo {
  /** Problem slug (`quota-exceeded`, `validation`, `network-error`, …) or a media `failure_code`. */
  type: string
  message: string
  status?: number
}

/** Immutable view of one upload, for rendering. */
export interface UploadTask {
  id: string
  file: File
  name: string
  size: number
  mime: string
  kind: MediaKind | null
  folderId: string | null
  /** Replacing this media (POST /media/{id}/replace) instead of creating new media. */
  replaceOf: string | null
  onDuplicate: 'reject' | 'keep'
  phase: UploadPhase
  /** 0..1 while hashing. */
  hashProgress: number
  uploadedBytes: number
  /** 0..1 of bytes uploaded. */
  progress: number
  uploadId: string | null
  mediaId: string | null
  media: Media | null
  /** Early duplicate (intent returned `duplicate_of`). */
  duplicateOf: Media | null
  /** Late duplicate (`rejected` + `failure_code: duplicate`). */
  duplicateOfId: string | null
  error: UploadErrorInfo | null
  createdAt: number
}

export interface IntentBody {
  filename: string
  size: number
  mime: string
  folder_id?: string | null
  sha256?: string
  on_duplicate: 'reject' | 'keep'
}

export type IntentResult = { type: 'intent'; intent: UploadIntent } | { type: 'duplicate'; media: Media }

export interface PartTarget {
  url: string
  method: string
  headers: Record<string, string>
}

export interface UploadDeps {
  createIntent(body: IntentBody, replaceOf: string | null): Promise<IntentResult>
  requestPartUrls(uploadId: string, partNumbers: number[]): Promise<UploadPartUrl[]>
  complete(uploadId: string, parts: { part_number: number; etag: string }[]): Promise<Media>
  abort(uploadId: string): Promise<void>
  getMedia(id: string): Promise<Media>
  /** `GET /media/uploads/{id}` — how a replacement's outcome is learned. */
  getIntent(uploadId: string): Promise<UploadIntent>
  /** PUT bytes to storage; resolves with the `ETag` response header (null if not exposed). Rejects with `PartUploadError`. */
  putPart(target: PartTarget, body: Blob, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<{ etag: string | null }>
  /** sha256 hex, or null when hashing isn't possible. */
  hash(file: File, onProgress: (ratio: number) => void, signal: AbortSignal): Promise<string | null>
  sleep(ms: number, signal?: AbortSignal): Promise<void>
  now(): number
}

export interface UploadManagerOptions {
  maxParallelParts?: number
  /** Uploads in the pre-processing pipeline at once (hash → intent → parts → complete). */
  maxActiveUploads?: number
  maxPartRetries?: number
  /** Files above this aren't hashed (sha256 is optional). */
  hashMaxBytes?: number
  pollIntervalMs?: number
  /** Throttle for progress-only change notifications (0 = immediate). */
  progressThrottleMs?: number
  /** Plan `upload_bytes` limit, checked before hashing (null = unlimited/unknown). */
  maxFileBytes?: () => number | null
  onQuotaExceeded?: (error: ApiError) => void
  /** Upload reached a terminal state server-side (ready / failed / rejected) or was canceled. */
  onSettled?: (task: UploadTask) => void
  /** A media row was created (intent) — lists can show the `uploading` placeholder. */
  onCreated?: (task: UploadTask) => void
}

export class PartUploadError extends Error {
  readonly status: number
  constructor(status: number, message = status ? `Storage responded ${status}` : 'Network error while uploading') {
    super(message)
    this.name = 'PartUploadError'
    this.status = status
  }
}

export const HASH_MAX_BYTES = 256 * 1024 * 1024

interface Internal {
  view: UploadTask
  controller: AbortController
  partSize: number
  partCount: number
  urls: Map<number, UploadPartUrl>
  etags: Map<number, string>
  loaded: Map<number, number>
  pending: number[]
  inflight: Set<number>
  fetchingUrls: Promise<void> | null
  partsDone: { resolve: () => void; reject: (e: unknown) => void } | null
  /** Part failures are fatal for the task once set. */
  partError: unknown
}

let seq = 0
const nextId = () => `up_${Date.now().toString(36)}_${(++seq).toString(36)}`

function isAbort(e: unknown): boolean {
  return (e as { name?: string } | null)?.name === 'AbortError'
}

function toErrorInfo(e: unknown): UploadErrorInfo {
  if (e instanceof ApiError) return { type: e.type, message: e.detail || e.title, status: e.status }
  if (e instanceof PartUploadError) return { type: 'network-error', message: e.message, status: e.status }
  if (e instanceof Error) return { type: 'unknown', message: e.message }
  return { type: 'unknown', message: 'Upload failed' }
}

export class UploadManager {
  private readonly deps: UploadDeps
  private readonly opts: Required<Omit<UploadManagerOptions, 'maxFileBytes' | 'onQuotaExceeded' | 'onSettled' | 'onCreated'>> &
    Pick<UploadManagerOptions, 'maxFileBytes' | 'onQuotaExceeded' | 'onSettled' | 'onCreated'>
  private tasks: Internal[] = []
  private listeners = new Set<() => void>()
  private snapshot: UploadTask[] = []
  private activePipelines = 0
  private inflightParts = 0
  private hashing = false
  private hashWaiters: (() => void)[] = []
  private emitTimer: ReturnType<typeof setTimeout> | null = null

  constructor(deps: UploadDeps, options: UploadManagerOptions = {}) {
    this.deps = deps
    this.opts = {
      maxParallelParts: 4,
      maxActiveUploads: 4,
      maxPartRetries: 3,
      hashMaxBytes: HASH_MAX_BYTES,
      pollIntervalMs: 2000,
      progressThrottleMs: 100,
      ...options,
    }
  }

  /* ------------------------------------------------------------ public api */

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): UploadTask[] => this.snapshot

  get(id: string): UploadTask | undefined {
    return this.find(id)?.view
  }

  add(files: Iterable<File>, target: { folderId?: string | null; replaceOf?: string | null } = {}): UploadTask[] {
    const added: Internal[] = []
    for (const file of files) {
      const view: UploadTask = {
        id: nextId(),
        file,
        name: file.name,
        size: file.size,
        mime: mimeForFile(file),
        kind: kindForFile(file),
        folderId: target.folderId ?? null,
        replaceOf: target.replaceOf ?? null,
        onDuplicate: 'reject',
        phase: 'queued',
        hashProgress: 0,
        uploadedBytes: 0,
        progress: 0,
        uploadId: null,
        mediaId: null,
        media: null,
        duplicateOf: null,
        duplicateOfId: null,
        error: null,
        createdAt: this.deps.now(),
      }
      added.push(this.fresh(view))
    }
    this.tasks.push(...added)
    this.emit()
    this.pump()
    return added.map((t) => t.view)
  }

  /** Abort an in-progress upload (no-op once the server is processing it). */
  cancel(id: string): void {
    const t = this.find(id)
    if (!t || !ACTIVE_PHASES.has(t.view.phase)) return
    const uploadId = t.view.uploadId
    t.controller.abort()
    this.patch(t, { phase: 'canceled', error: null })
    if (uploadId) void this.deps.abort(uploadId).catch(() => undefined)
    this.opts.onSettled?.(t.view)
    this.pump()
  }

  cancelAll(): void {
    for (const t of [...this.tasks]) this.cancel(t.view.id)
  }

  /** Start a failed / canceled upload again from scratch. */
  retry(id: string): void {
    const t = this.find(id)
    if (!t || !['failed', 'canceled', 'rejected'].includes(t.view.phase)) return
    this.restart(t, t.view.onDuplicate)
  }

  /** Duplicate found — store it as an independent copy (`on_duplicate: keep`). */
  uploadAnyway(id: string): void {
    const t = this.find(id)
    if (!t) return
    if (t.view.phase !== 'duplicate' && !(t.view.phase === 'rejected' && t.view.duplicateOfId)) return
    this.restart(t, 'keep')
  }

  dismiss(id: string): void {
    const t = this.find(id)
    if (!t) return
    if (ACTIVE_PHASES.has(t.view.phase)) this.cancel(id)
    t.controller.abort() // stops polling for `processing`
    this.tasks = this.tasks.filter((x) => x !== t)
    this.emit()
  }

  /** Removes finished uploads (ready, duplicates, failures, canceled). */
  clearFinished(): void {
    const keep = this.tasks.filter((t) => !FINISHED_PHASES.has(t.view.phase))
    if (keep.length === this.tasks.length) return
    this.tasks = keep
    this.emit()
  }

  hasActive(): boolean {
    return this.tasks.some((t) => ACTIVE_PHASES.has(t.view.phase))
  }

  /* -------------------------------------------------------------- internals */

  private fresh(view: UploadTask): Internal {
    return {
      view,
      controller: new AbortController(),
      partSize: 0,
      partCount: 0,
      urls: new Map(),
      etags: new Map(),
      loaded: new Map(),
      pending: [],
      inflight: new Set(),
      fetchingUrls: null,
      partsDone: null,
      partError: null,
    }
  }

  private restart(t: Internal, onDuplicate: 'reject' | 'keep') {
    t.controller.abort()
    const view: UploadTask = {
      ...t.view,
      onDuplicate,
      phase: 'queued',
      hashProgress: 0,
      uploadedBytes: 0,
      progress: 0,
      uploadId: null,
      mediaId: null,
      media: null,
      duplicateOf: null,
      duplicateOfId: null,
      error: null,
    }
    const next = this.fresh(view)
    this.tasks = this.tasks.map((x) => (x === t ? next : x))
    this.emit()
    this.pump()
  }

  private find(id: string): Internal | undefined {
    return this.tasks.find((t) => t.view.id === id)
  }

  private alive(t: Internal): boolean {
    return !t.controller.signal.aborted && this.tasks.includes(t)
  }

  private patch(t: Internal, patch: Partial<UploadTask>, progressOnly = false) {
    t.view = { ...t.view, ...patch }
    if (progressOnly) this.emitThrottled()
    else this.emit()
  }

  private emit() {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer)
      this.emitTimer = null
    }
    this.snapshot = this.tasks.map((t) => t.view)
    for (const l of this.listeners) l()
  }

  private emitThrottled() {
    if (this.opts.progressThrottleMs <= 0) return this.emit()
    if (this.emitTimer) return
    this.emitTimer = setTimeout(() => {
      this.emitTimer = null
      this.emit()
    }, this.opts.progressThrottleMs)
  }

  private pump() {
    while (this.activePipelines < this.opts.maxActiveUploads) {
      const next = this.tasks.find((t) => t.view.phase === 'queued')
      if (!next) break
      this.activePipelines++
      // Mark synchronously so the loop doesn't pick it twice.
      next.view = { ...next.view, phase: 'hashing' }
      void this.run(next).finally(() => {
        this.activePipelines--
        this.pump()
      })
    }
    this.pumpParts()
  }

  private fail(t: Internal, error: unknown, phase: 'failed' | 'rejected' = 'failed') {
    if (!this.tasks.includes(t) || t.view.phase === 'canceled') return
    const uploadId = t.view.uploadId
    const inUpload = t.view.phase === 'uploading' || t.view.phase === 'starting' || t.view.phase === 'hashing' || t.view.phase === 'completing'
    t.controller.abort()
    this.patch(t, { phase, error: toErrorInfo(error) })
    // Release the storage reservation of an intent we won't finish.
    if (uploadId && inUpload) void this.deps.abort(uploadId).catch(() => undefined)
    if (error instanceof ApiError && error.type === 'quota-exceeded') this.opts.onQuotaExceeded?.(error)
    this.opts.onSettled?.(t.view)
  }

  private async withHashSlot<T>(fn: () => Promise<T>): Promise<T> {
    while (this.hashing) await new Promise<void>((r) => this.hashWaiters.push(r))
    this.hashing = true
    try {
      return await fn()
    } finally {
      this.hashing = false
      this.hashWaiters.shift()?.()
    }
  }

  private async run(t: Internal): Promise<void> {
    const signal = t.controller.signal
    try {
      // 0. Plan limit pre-check (server stays authoritative).
      const max = this.opts.maxFileBytes?.() ?? null
      if (max !== null && t.view.size > max) {
        throw new ApiError(402, {
          type: 'https://signetix.io/problems/quota-exceeded',
          title: 'File too large for your plan',
          detail: `“${t.view.name}” is larger than your plan’s maximum upload size.`,
          quota: 'upload_bytes',
          limit: max,
        })
      }

      // 1. sha256 (optional — enables "already in library" before uploading anything).
      let sha256: string | null = null
      if (t.view.size > 0 && t.view.size <= this.opts.hashMaxBytes) {
        this.patch(t, { phase: 'hashing', hashProgress: 0 })
        sha256 = await this.withHashSlot(async () => {
          if (!this.alive(t)) return null
          try {
            return await this.deps.hash(t.view.file, (r) => this.alive(t) && this.patch(t, { hashProgress: r }, true), signal)
          } catch (e) {
            if (isAbort(e) || signal.aborted) throw e
            return null // hashing is best-effort
          }
        })
      }
      if (!this.alive(t)) return

      // 2. Intent.
      this.patch(t, { phase: 'starting', hashProgress: 1 })
      const body: IntentBody = {
        filename: t.view.name,
        size: t.view.size,
        mime: t.view.mime,
        on_duplicate: t.view.onDuplicate,
      }
      if (!t.view.replaceOf) body.folder_id = t.view.folderId
      if (sha256) body.sha256 = sha256
      const result = await this.deps.createIntent(body, t.view.replaceOf)
      if (!this.alive(t)) {
        if (result.type === 'intent') void this.deps.abort(result.intent.upload_id).catch(() => undefined)
        return
      }
      if (result.type === 'duplicate') {
        this.patch(t, { phase: 'duplicate', duplicateOf: result.media, progress: 1 })
        this.opts.onSettled?.(t.view)
        return
      }

      const intent = result.intent
      t.partSize = intent.part_size > 0 ? intent.part_size : Math.max(1, t.view.size)
      t.partCount = Math.max(1, intent.part_count || intent.parts.length || 1)
      for (const p of intent.parts) t.urls.set(p.part_number, p)
      t.pending = Array.from({ length: t.partCount }, (_, i) => i + 1)
      this.patch(t, { phase: 'uploading', uploadId: intent.upload_id, mediaId: intent.media_id, media: intent.media ?? null })
      this.opts.onCreated?.(t.view)

      // 3. Parts (shared queue).
      await new Promise<void>((resolve, reject) => {
        t.partsDone = { resolve, reject }
        if (t.pending.length === 0) resolve()
        this.pumpParts()
      })
      if (!this.alive(t)) return

      // 4. Complete.
      this.patch(t, { phase: 'completing', uploadedBytes: t.view.size, progress: 1 })
      const parts = [...t.etags.entries()].sort((a, b) => a[0] - b[0]).map(([part_number, etag]) => ({ part_number, etag }))
      const media = await this.deps.complete(intent.upload_id, parts)
      if (!this.alive(t)) return
      // A replacement's live media stays `ready` on its old revision — show it as processing.
      this.patch(t, { phase: 'processing', media: t.view.replaceOf ? null : media, mediaId: media.id ?? t.view.mediaId })
      // Processing is server-side: free the pipeline slot and poll in the background.
      void (t.view.replaceOf ? this.pollReplacement(t) : this.poll(t))
    } catch (e) {
      if (isAbort(e) || signal.aborted) return
      this.fail(t, e)
    }
  }

  /**
   * Replacement: the live media keeps serving its current revision, so the outcome
   * is read from the intent (`processed | failed | aborted | expired`).
   */
  private async pollReplacement(t: Internal): Promise<void> {
    const signal = t.controller.signal
    const uploadId = t.view.uploadId
    if (!uploadId) return
    let errors = 0
    try {
      for (;;) {
        await this.deps.sleep(this.opts.pollIntervalMs, signal)
        if (!this.alive(t)) return
        let intent: UploadIntent
        try {
          intent = await this.deps.getIntent(uploadId)
          errors = 0
        } catch (e) {
          if (isAbort(e)) return
          if (e instanceof ApiError && e.status === 404) throw e
          if (++errors >= 30) throw e
          continue
        }
        if (!this.alive(t)) return
        if (intent.status === 'processed') {
          const media = await this.deps.getMedia(t.view.mediaId ?? intent.media_id).catch(() => intent.media)
          if (!this.alive(t)) return
          this.patch(t, { phase: 'ready', media })
          break
        }
        if (intent.status === 'failed' || intent.status === 'aborted' || intent.status === 'expired') {
          const code = intent.failure_code ?? (intent.status === 'expired' ? 'upload_expired' : intent.status)
          this.patch(t, {
            // Policy rejections vs. technical failures (retryable).
            phase: ['processing_failed', 'upload_expired', 'aborted', 'expired', 'failed'].includes(code) ? 'failed' : 'rejected',
            error: { type: code, message: failureText(code, intent.failure_detail) },
          })
          break
        }
      }
    } catch (e) {
      if (isAbort(e) || signal.aborted) return
      this.fail(t, e)
      return
    }
    this.opts.onSettled?.(t.view)
  }

  /** Poll `GET /media/{id}` until ready | failed | rejected. */
  private async poll(t: Internal): Promise<void> {
    const signal = t.controller.signal
    const id = t.view.mediaId
    if (!id) return
    let media = t.view.media
    let errors = 0
    try {
      while (!media || !isTerminalStatus(media.status)) {
        await this.deps.sleep(this.opts.pollIntervalMs, signal)
        if (!this.alive(t)) return
        try {
          media = await this.deps.getMedia(id)
          errors = 0
          if (!this.alive(t)) return
          if (!isTerminalStatus(media.status)) this.patch(t, { media })
        } catch (e) {
          if (isAbort(e)) return
          if (e instanceof ApiError && e.status === 404) throw e
          // Transient: keep polling, but give up after a long streak.
          if (++errors >= 30) throw e
        }
      }
    } catch (e) {
      if (isAbort(e) || signal.aborted) return
      this.fail(t, e)
      return
    }
    if (media.status === 'ready') {
      this.patch(t, { phase: 'ready', media })
    } else {
      const duplicateOfId = media.failure_code === 'duplicate' ? (media.duplicate_of ?? null) : null
      t.view = { ...t.view, media, duplicateOfId }
      this.patch(t, {
        phase: media.status === 'rejected' ? 'rejected' : 'failed',
        error: { type: media.failure_code ?? media.status, message: failureText(media.failure_code, media.failure_detail) },
      })
    }
    this.opts.onSettled?.(t.view)
  }

  /* ------------------------------------------------------------- parts */

  private pumpParts() {
    while (this.inflightParts < this.opts.maxParallelParts) {
      const pick = this.pickPart()
      if (!pick) return
      void this.runPart(pick.task, pick.part)
    }
  }

  private pickPart(): { task: Internal; part: number } | null {
    for (const t of this.tasks) {
      if (t.view.phase !== 'uploading' || !t.partsDone || t.pending.length === 0 || t.partError) continue
      const part = t.pending[0]!
      if (!isUrlUsable(t.urls.get(part), this.deps.now())) {
        // Fetch a batch of fresh URLs; this task waits, others continue.
        if (!t.fetchingUrls) this.topUpUrls(t, part)
        continue
      }
      t.pending.shift()
      t.inflight.add(part)
      this.inflightParts++
      return { task: t, part }
    }
    return null
  }

  private topUpUrls(t: Internal, from: number): Promise<void> {
    if (t.fetchingUrls) return t.fetchingUrls
    const skip = new Set<number>([...t.etags.keys(), ...t.inflight])
    const numbers = partNumbersToRequest({ partCount: t.partCount, known: t.urls, skip, now: this.deps.now(), from })
    if (!numbers.includes(from)) numbers.unshift(from)
    const uploadId = t.view.uploadId!
    t.fetchingUrls = this.deps
      .requestPartUrls(uploadId, numbers)
      .then((parts) => {
        for (const p of parts) t.urls.set(p.part_number, p)
        if (!parts.some((p) => p.part_number === from)) throw new Error(`No upload URL returned for part ${from}`)
      })
      .catch((e) => {
        if (!isAbort(e) && this.alive(t)) {
          t.partError = e
          this.fail(t, e)
          t.partsDone?.reject(e)
        }
      })
      .finally(() => {
        t.fetchingUrls = null
        this.pumpParts()
      })
    return t.fetchingUrls
  }

  private progressOf(t: Internal): number {
    let sum = 0
    for (const n of t.loaded.values()) sum += n
    return Math.min(sum, t.view.size)
  }

  private async runPart(t: Internal, part: number): Promise<void> {
    const signal = t.controller.signal
    const [start, end] = partRange(part, t.partSize, t.view.size)
    const blob = t.view.file.slice(start, end)
    let attempt = 0
    try {
      for (;;) {
        const url = t.urls.get(part)
        try {
          if (!url || !isUrlUsable(url, this.deps.now())) {
            await this.topUpUrls(t, part)
            if (!this.alive(t) || t.partError) return
            continue
          }
          const { etag } = await this.deps.putPart({ url: url.url, method: url.method || 'PUT', headers: url.headers ?? {} }, blob, (loaded) => {
            if (!this.alive(t)) return
            t.loaded.set(part, loaded)
            const uploadedBytes = this.progressOf(t)
            this.patch(t, { uploadedBytes, progress: t.view.size ? uploadedBytes / t.view.size : 0 }, true)
          }, signal)
          t.etags.set(part, etag ?? '')
          t.loaded.set(part, partLength(part, t.partSize, t.view.size))
          const uploadedBytes = this.progressOf(t)
          this.patch(t, { uploadedBytes, progress: t.view.size ? uploadedBytes / t.view.size : 1 }, true)
          return
        } catch (e) {
          if (isAbort(e) || signal.aborted || !this.alive(t)) return
          attempt++
          t.loaded.set(part, 0)
          if (attempt > this.opts.maxPartRetries) throw e
          // 403/400 from storage usually means the presigned URL expired — refresh it.
          if (e instanceof PartUploadError && (e.status === 403 || e.status === 400)) t.urls.delete(part)
          await this.deps.sleep(retryDelay(attempt), signal)
        }
      }
    } catch (e) {
      if (isAbort(e) || signal.aborted) return
      if (!t.partError) {
        t.partError = e
        this.fail(t, e)
        t.partsDone?.reject(e)
      }
    } finally {
      t.inflight.delete(part)
      this.inflightParts--
      if (this.alive(t) && !t.partError && t.pending.length === 0 && t.inflight.size === 0 && t.etags.size >= t.partCount) {
        t.partsDone?.resolve()
      }
      this.pumpParts()
    }
  }
}
